/**
 * Shared @mention alias derivation for tested assets.
 *
 * Authoring UI, coverage scoring, and report rendering MUST use this module so
 * `@Ad 1` / `@Landing Page 2` resolve the same way after assets are renamed.
 */

export interface AssetMentionAliasInput {
  name: string;
  type: string;
  id?: string;
  stableKey?: string;
}

/**
 * Trailing lookahead after an @mention alias match.
 *
 * Allows end-of-string, whitespace, common punctuation, `]`, possessive
 * apostrophes (ASCII `'` and curly `’`) so `@Ad 1's` resolves to `Name's`,
 * closing quotes (`"`, `”`) and dashes (`-`, `–`, `—`: `@Kit Plus-branded`),
 * while still refusing to match mid-word (`@Ad 12`).
 *
 * Embed in `new RegExp(\`@\\\\s*(...)${ASSET_MENTION_TRAILING_LOOKAHEAD}\`)`.
 * Keep Python `strip_asset_mentions` and the revision translation's
 * `_MENTION_END` in sync when changing this.
 */
export const ASSET_MENTION_TRAILING_LOOKAHEAD =
  "(?=$|\\s|[.,!?;:)\\]'\u2019\"\u201d\u2013\u2014-])";

function escapeMentionAlias(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * One alias's pattern among the catalog's aliases: the alias, guarded so it
 * never matches where the text goes on into a longer alias that starts with
 * it (`@Kit` inside `@Kit Plus-branded` is no mention of Kit). Literal
 * provenance only: the guard compares the catalog's own aliases with the
 * text, letter case aside, as every mention pattern is compiled (`i`).
 */
export function assetMentionAliasPattern(
  alias: string,
  aliases: Iterable<string>,
): string {
  const lower = alias.toLowerCase();
  const guards = [...new Set([...aliases].map((other) => other.toLowerCase()))]
    .filter((other) => other.length > lower.length && other.startsWith(lower))
    .map((other) => `(?!${escapeMentionAlias(other.slice(lower.length))})`);
  return `${escapeMentionAlias(alias)}${guards.join("")}`;
}

function cleanText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** Normalize for ordinal matching only — do not truncate (slice length must not differ by app). */
function normalizeStableKeyForOrdinal(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function ordinalFromStableAssetKey(
  value: string | undefined,
): { kind: string; index: string } | null {
  const normalized = normalizeStableKeyForOrdinal(value ?? "");
  const match = normalized.match(/(?:^|_)(ad|landing_page)_?(\d+)$/);
  return match ? { kind: match[1], index: match[2] } : null;
}

/**
 * Aliases that may appear after `@` in question/option text for this asset.
 * Longer aliases should win when matching (e.g. Landing Page 12 before Landing Page 1).
 */
export function buildAssetMentionAliases(
  asset: AssetMentionAliasInput,
): Array<string> {
  const aliases = [cleanText(asset.name)].filter(Boolean);
  const type = String(asset.type);
  const stableCandidates = [
    asset.stableKey,
    asset.id,
    asset.id?.replace(/^asset_/, ""),
  ];
  const ordinal = stableCandidates
    .map((candidate) => ordinalFromStableAssetKey(candidate))
    .find(Boolean);

  if (ordinal?.kind === "ad" || type === "ad") {
    if (ordinal?.index) {
      aliases.push(`Ad ${ordinal.index}`, `Ad${ordinal.index}`);
    }
  }
  if (ordinal?.kind === "landing_page" || type === "landing_page") {
    if (ordinal?.index) {
      aliases.push(
        `Landing Page ${ordinal.index}`,
        `Landing Page${ordinal.index}`,
        // The English twin-facing ordinal is "Page N" (the registry's `ordinal`); authors
        // and compiled copy use it interchangeably with "Landing Page N".
        `Page ${ordinal.index}`,
        `Page${ordinal.index}`,
      );
    }
  }

  const seen = new Set<string>();
  return aliases.filter((alias) => {
    const key = alias.toLowerCase();
    if (!alias || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}
